import React from "react";
import { Button, Result } from "antd";

type Props = React.PropsWithChildren<{
  /**
   * A change clears a caught error - the route, so navigating away from a page
   * that failed gives the next one a fresh start. A reset rather than a `key`,
   * which would remount the page on every navigation, including between two
   * specimens that share one SpecimenDetail.
   */
  resetKey?: unknown;
}>;

type State = { error?: Error };

/**
 * Keeps a render error in one page from taking the whole app down with it.
 *
 * Without a boundary React unmounts the entire tree on an uncaught render error,
 * and what the user sees is a white page with nothing to click - the same symptom
 * as an auth failure, and as hard to tell apart from one. MainLayout wraps its
 * page in this so the Navbar survives and the user can still go somewhere else.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Page failed to render", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Result
        status="error"
        title="Something went wrong"
        subTitle="This page could not be displayed. Reloading usually fixes it."
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    );
  }
}

export default ErrorBoundary;
